// Paginated list of conversations with audio-coverage rollups and a
// click-through to the per-conversation line editor.
//
// Two tabs:
//   Live    — main-course conversations the learner flow actually
//             serves (mini_course_id IS NULL, chapter & lecture set,
//             variant=1).
//   Drafts  — main-course conversations that are not Live yet —
//             missing chapter/lecture or stuck on a non-1 variant.
//
// Mini-course conversations live under /admin/mini-courses/:id.

import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { getContentConversations } from '../adminApi';
import { Loading, Err } from './Overview';
import { Pager } from './Users';

const PAGE_SIZE = 50;

const TABS = [
    { key: 'live', label: 'Live' },
    { key: 'drafts', label: 'Drafts' },
];

function readTabFromQuery(search) {
    const params = new URLSearchParams(search);
    const t = params.get('tab');
    return TABS.find((x) => x.key === t) ? t : 'live';
}

function describeDraftReason(c) {
    if (c.chapter == null || c.lecture == null) return 'no chapter/lecture';
    if (c.variant !== 1) return `variant ${c.variant}`;
    return 'draft';
}

export default function ContentConversations() {
    const history = useHistory();
    const location = useLocation();

    const [tab, setTab] = useState(() => readTabFromQuery(location.search));
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    useEffect(() => {
        // Keep tab in sync if the user hits back/forward.
        setTab(readTabFromQuery(location.search));
    }, [location.search]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setData(null);
            setErr(null);
            try {
                const d = await getContentConversations({
                    q: search,
                    status: tab,
                    page,
                    size: PAGE_SIZE,
                });
                if (!cancelled) setData(d);
            } catch (e) {
                if (!cancelled) setErr(e?.message || 'Failed to load');
            }
        })();
        return () => { cancelled = true; };
    }, [search, page, tab]);

    const switchTab = (next) => {
        const params = new URLSearchParams(location.search);
        params.set('tab', next);
        history.replace({ pathname: location.pathname, search: '?' + params.toString() });
        setPage(1);
    };

    const onSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(q);
    };

    if (err) return <Err msg={err} />;

    return (
        <div>
            <h2 style={styles.h2}>Content · conversations</h2>
            <p style={styles.intro}>
                Live conversations are the ones the main-course flow
                serves to learners. Drafts are everything else — set a
                chapter/lecture (and variant 1) to promote one.
                Mini-course conversations are managed under{' '}
                <Link to="/admin/mini-courses" style={styles.link}>Mini Courses</Link>.
            </p>

            <div style={styles.tabRow}>
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => switchTab(t.key)}
                        style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : null) }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <form onSubmit={onSearch} style={styles.searchRow}>
                <input
                    style={styles.input}
                    placeholder="Search title / tags…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <button type="submit" style={styles.btn}>Search</button>
                {search ? (
                    <button
                        type="button"
                        style={styles.btnLink}
                        onClick={() => { setQ(''); setSearch(''); setPage(1); }}
                    >
                        Clear
                    </button>
                ) : null}
            </form>

            {!data ? <Loading /> : (
                <>
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <Th>Status</Th>
                                    <Th>Ch/Lec</Th>
                                    <Th>Title</Th>
                                    <Th>Tags</Th>
                                    <Th>Speakers</Th>
                                    <Th align="right">Lines</Th>
                                    <Th align="right">Audio</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.length === 0 ? (
                                    <tr>
                                        <Td colSpan={7} style={{ textAlign: 'center', color: '#888' }}>
                                            No conversations.
                                        </Td>
                                    </tr>
                                ) : data.items.map((c) => (
                                    <tr key={c.id}>
                                        <Td>
                                            <StatusBadge live={tab === 'live'} reason={describeDraftReason(c)} />
                                        </Td>
                                        <Td>
                                            {c.chapter != null && c.lecture != null
                                                ? `${c.chapter}/${c.lecture}${c.variant > 1 ? `·v${c.variant}` : ''}`
                                                : <span style={{ color: '#bbb' }}>—</span>}
                                        </Td>
                                        <Td>
                                            <Link to={`/admin/content/conversations/${c.id}`} style={styles.link}>
                                                {c.title || `#${c.id}`}
                                            </Link>
                                        </Td>
                                        <Td style={{ color: '#888', fontSize: 12 }}>{c.tags || '—'}</Td>
                                        <Td style={{ color: '#444' }}>
                                            {[c.left_actor, c.right_actor].filter(Boolean).join(' · ') || '—'}
                                        </Td>
                                        <Td align="right">{c.n_lines}</Td>
                                        <Td align="right">
                                            <AudioBadge
                                                covered={c.n_lines_with_audio}
                                                total={c.n_lines}
                                            />
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pager
                        page={page}
                        size={PAGE_SIZE}
                        total={data.total}
                        onChange={setPage}
                    />
                </>
            )}
        </div>
    );
}

function StatusBadge({ live, reason }) {
    if (live) {
        return <span style={{ ...styles.badge, ...styles.badgeLive }}>Live</span>;
    }
    return (
        <span style={{ ...styles.badge, ...styles.badgeDraft }} title={reason}>
            Draft
        </span>
    );
}

function AudioBadge({ covered, total }) {
    if (total === 0) return <span style={{ color: '#bbb' }}>—</span>;
    const full = covered >= total;
    const empty = covered === 0;
    const color = full ? '#2e7d32' : empty ? '#c0392b' : '#ed8936';
    return (
        <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {covered}/{total}
        </span>
    );
}

function Th({ children, align }) {
    return <th style={{ ...styles.th, textAlign: align || 'left' }}>{children}</th>;
}
function Td({ children, align, style, colSpan }) {
    return <td colSpan={colSpan} style={{ ...styles.td, textAlign: align || 'left', ...(style || {}) }}>{children}</td>;
}

const styles = {
    h2: { marginTop: 0, color: '#222' },
    intro: { color: '#666', fontSize: 13, marginTop: -6, marginBottom: 14, lineHeight: 1.5 },
    tabRow: { display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #eee' },
    tab: { padding: '8px 14px', border: 'none', background: 'transparent', color: '#666', cursor: 'pointer', fontWeight: 600, fontSize: 13, borderBottom: '2px solid transparent', marginBottom: -1 },
    tabActive: { color: '#d97447', borderBottomColor: '#d97447' },
    searchRow: { display: 'flex', gap: 8, marginBottom: 12 },
    input: { flex: '0 0 280px', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc' },
    btn: { padding: '8px 14px', borderRadius: 6, border: '1px solid #d97447', background: '#fff', color: '#d97447', fontWeight: 600, cursor: 'pointer' },
    btnLink: { padding: '8px 0', border: 'none', background: 'transparent', color: '#888', cursor: 'pointer' },
    tableWrap: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 12px', borderBottom: '1px solid #eee', color: '#888', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 },
    td: { padding: '10px 12px', borderBottom: '1px solid #f3f3f3', color: '#222' },
    link: { color: '#d97447', textDecoration: 'none', fontWeight: 600 },
    badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' },
    badgeLive: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
    badgeDraft: { backgroundColor: '#fff3e0', color: '#d97447' },
};
